// src/pages/storePages/OrdersPage.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { storeGetMyOrders } from "../../storeAPIs/StoreOrdersApis";
import OrderDetailDialog from "./manageStore/OrderDetailDialog";
import "../../static/storecss/OrdersPage.css";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "confirmed", label: "Confirmed" },
];

const METHOD_LABELS = {
  mpesa: "M-Pesa",
  bank: "Bank",
  paypal: "PayPal",
};

export default function OrdersPage() {
  const { isLoggedIn, isLoading } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  // 👇 Which order's modal is open (null = closed)
  const [detailId, setDetailId] = useState(null);

  // Gate
  useEffect(() => {
    if (isLoading) return;
    if (!isLoggedIn) navigate("/login?next=/orders", { replace: true });
  }, [isLoading, isLoggedIn, navigate]);

  // Load orders
  useEffect(() => {
    if (isLoading || !isLoggedIn) return;
    setLoading(true);
    setError(null);
    storeGetMyOrders()
      .then((res) => {
        const data = res.data;
        setOrders(Array.isArray(data) ? data : data?.results ?? []);
      })
      .catch((err) => {
        setError(err.message || "Could not load your orders.");
      })
      .finally(() => setLoading(false));
  }, [isLoading, isLoggedIn]);

  // Filter + search
  const visibleOrders = useMemo(() => {
    let list = orders;

    if (statusFilter === "pending") {
      list = list.filter((o) => !o.is_confirmed);
    } else if (statusFilter === "confirmed") {
      list = list.filter((o) => o.is_confirmed);
    }

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((o) => {
        const haystack = [
          String(o.id),
          o.transaction_id,
          o.payment_method_display,
          o.customer_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return list;
  }, [orders, statusFilter, query]);

  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => !o.is_confirmed).length;
    const confirmed = orders.filter((o) => o.is_confirmed).length;
    const spent = orders
      .filter((o) => o.is_confirmed)
      .reduce((s, o) => s + Number(o.total || 0), 0);
    return { total, pending, confirmed, spent };
  }, [orders]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  if (isLoading) {
    return (
      <div className="order-page">
        <div className="order-page-loading">
          <span className="order-page-spinner" />
          Checking session…
        </div>
      </div>
    );
  }

  if (!isLoggedIn) return null;

  return (
    <div className="order-page">
      {/* ══ Header ══ */}
      <header className="order-page-header">
        <div className="order-page-header-text">
          <h1>My Orders</h1>
          <p>Track your purchases and payment status.</p>
        </div>
        <Link to="/store" className="order-page-header-cta">
          <i className="fa-solid fa-store" aria-hidden="true" />
          Continue shopping
        </Link>
      </header>

      {/* ══ Stats ══ */}
      <section className="order-page-stats" aria-label="Order summary">
        <div className="order-page-stat">
          <span className="order-page-stat-label">Total orders</span>
          <span className="order-page-stat-value">{stats.total}</span>
        </div>
        <div className="order-page-stat order-page-stat--warn">
          <span className="order-page-stat-label">Pending</span>
          <span className="order-page-stat-value">{stats.pending}</span>
        </div>
        <div className="order-page-stat order-page-stat--ok">
          <span className="order-page-stat-label">Confirmed</span>
          <span className="order-page-stat-value">{stats.confirmed}</span>
        </div>
        <div className="order-page-stat">
          <span className="order-page-stat-label">Total spent</span>
          <span className="order-page-stat-value order-page-stat-value--money">
            KES {stats.spent.toLocaleString()}
          </span>
        </div>
      </section>

      {/* ══ Toolbar ══ */}
      <div className="order-page-toolbar">
        <div
          className="order-page-filters"
          role="tablist"
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map((f) => {
            const showCount =
              (f.id === "pending" && stats.pending > 0) ||
              (f.id === "confirmed" && stats.confirmed > 0) ||
              (f.id === "all" && stats.total > 0);

            const count =
              f.id === "pending"
                ? stats.pending
                : f.id === "confirmed"
                ? stats.confirmed
                : stats.total;

            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={statusFilter === f.id}
                className={`order-page-filter ${
                  statusFilter === f.id ? "is-active" : ""
                }`}
                onClick={() => setStatusFilter(f.id)}
              >
                <span>{f.label}</span>
                {showCount && (
                  <span className="order-page-filter-count">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="order-page-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search order #, reference, method…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search orders"
          />
          {query && (
            <button
              type="button"
              className="order-page-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* ══ Body ══ */}
      {loading ? (
        <div className="order-page-loading">
          <span className="order-page-spinner" />
          Loading your orders…
        </div>
      ) : error ? (
        <div className="order-page-alert">{error}</div>
      ) : visibleOrders.length === 0 ? (
        <div className="order-page-empty">
          <div className="order-page-empty-icon">
            <i className="fa-solid fa-receipt" aria-hidden="true" />
          </div>
          <h2>{orders.length === 0 ? "No orders yet" : "No matches"}</h2>
          <p>
            {orders.length === 0
              ? "When you place your first order, it'll show up here."
              : "Try a different search or filter."}
          </p>
          {orders.length === 0 ? (
            <Link to="/store" className="order-page-btn order-page-btn--primary">
              <i className="fa-solid fa-store" aria-hidden="true" />
              Browse the store
            </Link>
          ) : (
            <button
              type="button"
              className="order-page-btn order-page-btn--ghost"
              onClick={() => {
                setQuery("");
                setStatusFilter("all");
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="order-page-table-wrap">
          <table className="order-page-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>Method</th>
                <th>Reference</th>
                <th className="order-page-th-right">Total</th>
                <th>Status</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((o) => (
                <tr key={o.id}>
                  <td className="order-page-cell-id">
                    <span className="order-page-order-id">#{o.id}</span>
                  </td>
                  <td className="order-page-cell-muted">
                    {formatDate(o.created_at)}
                  </td>
                  <td>
                    <span
                      className={`order-page-method order-page-method--${o.payment_method}`}
                    >
                      {METHOD_LABELS[o.payment_method] ||
                        o.payment_method_display}
                    </span>
                  </td>
                  <td className="order-page-cell-ref">
                    {o.transaction_id ? (
                      <code>{o.transaction_id}</code>
                    ) : (
                      <span className="order-page-cell-muted">—</span>
                    )}
                  </td>
                  <td className="order-page-cell-total">
                    KES {Number(o.total).toLocaleString()}
                  </td>
                  <td>
                    <span
                      className={`order-page-status ${
                        o.is_confirmed ? "is-confirmed" : "is-pending"
                      }`}
                    >
                      {o.is_confirmed ? "Confirmed" : "Pending"}
                    </span>
                  </td>
                  <td className="order-page-cell-action">
                    <button
                      type="button"
                      className="order-page-view-link"
                      onClick={() => setDetailId(o.id)}
                      title={`View order #${o.id}`}
                    >
                      View
                      <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ══ Order detail modal (read-only for customers) ══ */}
      <OrderDetailDialog
        open={!!detailId}
        orderId={detailId}
        onClose={() => setDetailId(null)}
        readOnly
      />
    </div>
  );
}